// ---------------------------------------------------------------------------
// /campaigns — index route (OPUS-D04)
// ---------------------------------------------------------------------------
// Lists all campaigns for the signed-in tenant with a state filter dropdown.
// Clicking a row navigates to `/campaigns/$campaignId` (detail route not
// landed yet — 404 is the expected placeholder until OPUS-D05+).
// ---------------------------------------------------------------------------

import type { RootRoute } from "@tanstack/react-router";
import { createRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { CampaignsTable } from "@/features/campaigns/CampaignsTable";
import {
	CAMPAIGN_STATES,
	STATE_LABEL,
	useCampaignsList,
	type CampaignState,
} from "@/features/campaigns/api";

type StateFilter = CampaignState | "all";

function CampaignsIndexPage() {
	const [state, setState] = useState<StateFilter>("all");
	const [queryText, setQueryText] = useState("");
	const [debouncedQ, setDebouncedQ] = useState("");

	// Debounce the search input so we do not spam the api while the user types.
	// 250ms balances responsiveness against needless fetches.
	useMemo(() => {
		const t = setTimeout(() => setDebouncedQ(queryText.trim()), 250);
		return () => clearTimeout(t);
	}, [queryText]);

	const query = useCampaignsList({
		state: state === "all" ? undefined : state,
		q: debouncedQ.length > 0 ? debouncedQ : undefined,
		limit: 50,
	});

	const rows = query.data?.items ?? [];

	return (
		<div className="mx-auto w-full max-w-[1200px] px-6 py-10">
			<header className="mb-6 flex flex-wrap items-end justify-between gap-4">
				<div>
					<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight">
						Campaigns
					</h1>
					<p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
						Manage your active campaigns, monitor performance, and adjust state.
					</p>
				</div>
				<Toolbar
					state={state}
					onState={setState}
					queryText={queryText}
					onQueryText={setQueryText}
				/>
			</header>

			<CampaignsTable
				rows={rows}
				isLoading={query.isLoading}
				isError={query.isError}
				error={query.error ?? undefined}
				onRetry={() => query.refetch()}
			/>

			<footer className="mt-4 flex items-center justify-between text-xs text-[color:var(--color-text-muted)]">
				<span>
					{query.isFetching
						? "Refreshing…"
						: `${rows.length} campaign${rows.length === 1 ? "" : "s"} shown`}
				</span>
				{query.data?.next_cursor ? (
					<span>More available — pagination lands in OPUS-D05.</span>
				) : null}
			</footer>
		</div>
	);
}

// ---- toolbar --------------------------------------------------------------

interface ToolbarProps {
	state: StateFilter;
	onState: (s: StateFilter) => void;
	queryText: string;
	onQueryText: (s: string) => void;
}

function Toolbar({ state, onState, queryText, onQueryText }: ToolbarProps) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			<label className="sr-only" htmlFor="campaigns-search">
				Search campaigns
			</label>
			<input
				id="campaigns-search"
				value={queryText}
				onChange={(e) => onQueryText(e.target.value)}
				placeholder="Search campaigns"
				className="w-64 rounded-[6px] border bg-[color:var(--color-surface)] px-3 py-1.5 text-sm text-[color:var(--color-text)] placeholder:text-[color:var(--color-text-faint)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand-emerald)]/30"
				style={{ borderColor: "var(--color-border-strong)" }}
			/>
			<label className="sr-only" htmlFor="campaigns-state">
				Filter by state
			</label>
			<select
				id="campaigns-state"
				value={state}
				onChange={(e) => onState(e.target.value as StateFilter)}
				className="rounded-[6px] border bg-[color:var(--color-surface)] px-3 py-1.5 text-sm text-[color:var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand-emerald)]/30"
				style={{ borderColor: "var(--color-border-strong)" }}
			>
				<option value="all">All states</option>
				{CAMPAIGN_STATES.map((s) => (
					<option key={s} value={s}>
						{STATE_LABEL[s]}
					</option>
				))}
			</select>
		</div>
	);
}

// ---- tanstack-router route export -----------------------------------------
// The dash scaffold (opD01) uses programmatic router assembly, so we expose a
// factory that the scaffold owner can wire in with one line:
//
//   import { createCampaignsIndexRoute } from "./routes/campaigns.index";
//   const campaignsIndexRoute = createCampaignsIndexRoute(rootRoute);
//   rootRoute.addChildren([..., campaignsIndexRoute]);

// biome-ignore lint/suspicious/noExplicitAny: RootRoute generics are noisy and
// the scaffold consumer only needs a simple "pass the parent" ergonomic.
export function createCampaignsIndexRoute(parent: RootRoute<any, any, any, any, any, any, any, any, any>) {
	return createRoute({
		getParentRoute: () => parent,
		path: "/campaigns",
		component: CampaignsIndexPage,
	});
}

export { CampaignsIndexPage };
export default CampaignsIndexPage;
